import type { AgentProfileBlockProps, SiteProfileData } from '../types';
import { theme, sectionColors, cardTextColor, PAGE_TEXT_COLOR } from '../theme';
import { PhoneIcon, MailIcon } from './contactIcons';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** True when the profile has anything this card would show. */
export function profileHasContent(p: SiteProfileData | null | undefined): boolean {
  if (!p) return false;
  return [p.full_name, p.headshot_url, p.bio, p.phone, p.email].some((v) => typeof v === 'string' && v.trim() !== '');
}

/**
 * The site's own agent profile as one card: headshot (or initials), name, brokerage, licence,
 * bio, phone and email.
 *
 * LIVE by design. The block stores only its heading and switches; the person comes from the
 * host's site config (`BlockData.profile`) at render time, so a Profile edit shows on every page
 * carrying this block and nothing is frozen into the page. This is the "Meet Our Team" card the
 * built-in About page has always rendered, now a section the Studio can move, hide and recolour.
 *
 * Renders nothing when the Profile is empty, rather than a nameless card.
 */
export function AgentProfileBlock({
  props,
  profile,
  companyName,
}: {
  props: AgentProfileBlockProps;
  profile?: SiteProfileData | null;
  companyName?: string | null;
}) {
  const { heading, showBio = true, showLicense = true, showContact = true } = props;
  if (!profile || !profileHasContent(profile)) return null;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const name = profile.full_name?.trim() ?? '';
  const brokerage = profile.brokerage_name?.trim() || companyName?.trim() || '';
  const license = profile.license_number?.trim();

  return (
    <section className="px-6 py-16" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-7xl">
        {heading ? (
          <h2 className="mb-10 text-center text-3xl font-bold" style={{ fontFamily: theme.headingFont }}>
            {heading}
          </h2>
        ) : null}
        <div
          className="blk-card mx-auto max-w-3xl overflow-hidden rounded-2xl border border-black/10 shadow-sm"
          // The page background; its words stay readable on it, whatever the section's text.
          style={{ backgroundColor: theme.background, color: cardTextColor(props, { surfaceInk: PAGE_TEXT_COLOR }) }}
        >
          <div className="md:flex">
            <div
              className="flex shrink-0 items-center justify-center p-8 md:w-60"
              style={{ backgroundColor: `color-mix(in srgb, ${theme.primary} 7%, transparent)` }}
            >
              {profile.headshot_url ? (
                <img
                  src={profile.headshot_url}
                  alt={name}
                  className="h-36 w-36 rounded-full object-cover shadow-md"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className="flex h-36 w-36 items-center justify-center rounded-full text-4xl font-bold shadow-md"
                  style={{ backgroundColor: theme.primary, color: theme.onPrimary }}
                  aria-hidden="true"
                >
                  {initialsOf(name)}
                </div>
              )}
            </div>
            <div className="flex-1 p-8">
              {name ? (
                <h3 className="mb-1 text-2xl font-bold" style={{ fontFamily: theme.headingFont }}>
                  {name}
                </h3>
              ) : null}
              {brokerage ? (
                <p className="mb-1 text-sm font-semibold" style={{ color: theme.primary }}>
                  {brokerage}
                </p>
              ) : null}
              {showLicense && license ? (
                <p className="mb-4 text-xs opacity-60">
                  License #{license}
                  {profile.license_state ? ` · ${profile.license_state}` : ''}
                </p>
              ) : null}
              {showBio && profile.bio?.trim() ? (
                <p className="mb-6 text-sm leading-relaxed opacity-80">{profile.bio}</p>
              ) : null}
              {showContact && (profile.phone || profile.email) ? (
                <div className="flex flex-col gap-2.5">
                  {profile.phone ? (
                    <a href={`tel:${profile.phone}`} className="inline-flex items-center gap-2 text-sm hover:underline">
                      <PhoneIcon className="h-4 w-4 shrink-0 opacity-50" />
                      {profile.phone}
                    </a>
                  ) : null}
                  {profile.email ? (
                    <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-2 text-sm hover:underline">
                      <MailIcon className="h-4 w-4 shrink-0 opacity-50" />
                      {profile.email}
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
